import { moveItemInArray, CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, Input, QueryList, ViewChildren, OnChanges, Output, EventEmitter, Injector, SimpleChanges } from '@angular/core';
import { ChecklistBuilderService } from '../checklist-builder.service';
import { ChecklistTemplate } from '../../checklists';
import { ChecklistBuilderSectionComponent } from './checklist-builder-section/checklist-builder-section.component';
import { WFSectionLocalResource } from 'src/app/components/workflow/workflow';
import { BaseComponent } from 'src/app/common/base/base.component';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { TransactionLogService } from 'src/app/services/logs/transaction-log.service';
import { ChecklistElement, SelectedElement } from '../checklist-builder-map/checklist-builder-map.component';
import { WFError } from '../checklist-builder.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'checklist-builder-sections',
  templateUrl: './checklist-builder-sections.component.html',
  styleUrls: ['./checklist-builder-sections.component.scss']
})
export class ChecklistBuilderSectionsComponent extends BaseComponent implements OnChanges {

  @Input() sections!: WFSectionLocalResource[];
  @Input() currentVersion!: ChecklistTemplate;
  @Input() wfTableID!: number;
  @Input() disabled!: boolean;
  @Input() showDeleted?: boolean;
  @Output() errorsDetected = new EventEmitter<WFError[]>();

  @ViewChildren(ChecklistBuilderSectionComponent) sectionComponents!: QueryList<ChecklistBuilderSectionComponent>;
  sectionComponent?: ChecklistBuilderSectionComponent;

  sectionDropLists: string[] = [];
  stepDropLists: string[] = [];
  private checklistTemplateSub?: Subscription;

  constructor(
    protected override injector: Injector,
    public service: ChecklistBuilderService,
    private logService: TransactionLogService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.checklistTemplateSub = this.service.getChecklistTemplate()
      ?.subscribe(data => {
        this.sections = data?.wfTable?.wfTableLocal?.wfSectionLocals ?? [];
        this.getSections();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sections'] && !changes['sections'].firstChange) {
      this.getSections();
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.checklistTemplateSub?.unsubscribe();
  }

  getSections() {
    this.sections = [...new Map(
      this.sections?.map(section => [section.id, section]) // Using the section `id` as the key to ensure uniqueness
    ).values() // Extract the unique values
    ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    this.renumberArray().then(() => {
      this.updateDropLists();
      if (this.sections.length && !this.service.selectedNode?.sectionNode) {
        this.sections.map(s => s.collapsed = true);
        this.service.selectedNode = { sectionNode: { id: this.sections[0].id, type: ChecklistElement.SECTION } } as SelectedElement;
        this.sections[0].collapsed = false;
      }
    });
    this.checkErrors();
  }

  add() {
    this.service.createSection(this.wfTableID).subscribe(async data => {
      this.sections.push(data);
      await this.renumberArray();
      this.updateDropLists();
    });
  }

  async deleted(section: WFSectionLocalResource) {
    const index = this.sections.findIndex(x => x.id === section.id);
    if (index !== -1) {
      this.sections.splice(index, 1);
    }
    await this.renumberArray();
    this.updateDropLists();
  }

  async drop(event: CdkDragDrop<WFSectionLocalResource[]>) {
    //this.loading.emit(true);
    if (event?.previousIndex !== event?.currentIndex) {
      moveItemInArray(this.sections, event.previousIndex, event.currentIndex);
      await this.renumberArray();
      this.service.selectedNode = undefined;
    }
  }

  updateDropLists() {
    this.sectionDropLists = this.sections?.map(_ => 'section-' + _.id);
    this.sections?.map(s => {
      if (s.wfStepLocals)
        this.stepDropLists = this.stepDropLists.concat(s.wfStepLocals.map(_ => 'step-' + _.id));
    });
  }

  async renumberArray() {
    let order = 0;
    const sectionsToUpdate: WFSectionLocalResource[] = [];
    this.sections.filter(s => !s.logID).map(s => {
      const number = order + 1;
      if (s.order != order || s.number != number) {
        s.order = order;
        s.number = order + 1;
        sectionsToUpdate.push(s);
      }
      order++;
    });

    if (sectionsToUpdate.length)
      await this.service.updateSections(sectionsToUpdate).toPromise();

    this.sections = this.sections?.sort((a, b) => ((a.order ?? 0) - (b.order ?? 0)));
  }

  refreshPreviousStep(stepID: number) {
    this.sectionComponents.map(section => {
      const stepComponent = section.stepsComponent.stepComponents.find(s => s.step.id === stepID);
      stepComponent?.tasksComponent.refreshTasks();
    });
  }

  restore(section: WFSectionLocalResource) {
    if (section.logID) {
      //this.loading.emit(true);
      this.service.restoreSection(section.logID).toPromise().then(async data => {
        if (data) {
          await this.renumberArray();
          data.order = (data.order ?? 0) - 1;
          this.alert.success('Step: <b>' + data.name + '</b> has been Restored!.');
          this.scrollToSection(data.id);
        }
        //this.loading.emit(false);
      });
    }
  }

  deleteForever(section: WFSectionLocalResource) {
    const yesno = this.dialog.open(YesNoDialogComponent, {
      width: "400px",
      data: {
        message: 'Are you sure you want to Remove this Deleted Section Forever?',
        icon: "stop",
      },
    });
    yesno.afterClosed().subscribe((data) => {
      if (data) {
        if (section.logID)
          this.logService.UpdateStatus(section.logID).toPromise().then(() => {
          });
      }
    });
  }

  scrollToSection(sectionID: number) {
    setTimeout(() => {
      const element = document.getElementById('section-' + sectionID);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    }, 500);
  }

  async refreshRestored(section: WFSectionLocalResource) {
    this.sections.push(section);
    this.sections = this.sections.sort((a, b) => this.utils.sort(a.order, b.order));
    await this.renumberArray();
  }

  showSection(section: WFSectionLocalResource) {
    if (this.service.selectedNode?.sectionNode)
      this.service.selectedNode.sectionNode.id = section.id;
    else
      this.service.selectedNode = { sectionNode: { id: section.id, type: ChecklistElement.SECTION } } as SelectedElement;

    section.collapsed = false;
  }

  hideSection(section: WFSectionLocalResource) {
    if (this.service.selectedNode?.sectionNode?.id == section.id)
      this.service.selectedNode = undefined;
    section.collapsed = true;
  }

  sectionLoading(section: WFSectionLocalResource) {
    setTimeout(() => {
      const sectionComponent = this.sectionComponents?.find(s => s.section.id == section.id);
      if (sectionComponent) this.sectionComponent = sectionComponent;
      return !sectionComponent || sectionComponent.sectionLoading;
    }, 2000);
  }

  checkErrors(): void {
    if (!this.currentVersion) return;

    const statuses = this.service.getChecklistExecStatuses(this.currentVersion);

    // Signature conditions reference tasks by Tag from anywhere in the template, so validation
    // needs the whole set rather than just the task being checked.
    const allTasks = (this.sections ?? [])
      .flatMap(s => s.wfStepLocals ?? [])
      .flatMap(s => s.wfTaskLocals ?? []);

    for (const section of this.sections ?? []) {
      for (const step of section.wfStepLocals ?? []) {
        for (const task of step.wfTaskLocals ?? []) {
          const err = this.service.hasTaskErrors(task, statuses, allTasks) as WFError | null;

          // recompute from scratch each time:
          task.errors = err ? [err] : [];
        }

        const taskErrors = (step.wfTaskLocals ?? []).flatMap(t => t.errors ?? []);
        const stepErr = this.service.hasStepErrors(step, statuses) as WFError | null;

        step.errors = this.mergeWFErrors(stepErr ? [...taskErrors, stepErr] : taskErrors);
      }

      const stepErrors = (section.wfStepLocals ?? []).flatMap(st => st.errors ?? []);
      section.errors = this.mergeWFErrors(stepErrors);
    }

    const sectionErrors = (this.sections ?? []).flatMap(s => s.errors ?? []);
    this.currentVersion.errors = this.mergeWFErrors(sectionErrors);

    this.errorsDetected.emit(this.currentVersion.errors);
  }



  private mergeWFErrors(items: WFError[]): WFError[] {
    const map = new Map<string, WFError>();

    for (const e of items) {
      if (!e) continue;

      const key = `${e.sectionId}|${e.stepId ?? ''}|${e.taskId ?? ''}`;
      const existing = map.get(key);

      if (!existing) {
        map.set(key, {
          sectionId: e.sectionId,
          stepId: e.stepId,
          taskId: e.taskId,
          errors: [...(e.errors ?? [])],
        });
      } else {
        existing.errors.push(...(e.errors ?? []));
      }
    }

    // Deduplicate messages inside each WFError
    for (const e of map.values()) {
      e.errors = Array.from(new Set(e.errors.filter(Boolean)));
    }

    return Array.from(map.values());
  }


}
